import api from '@/api/api.js';
import _ from 'lodash';
import moment from 'moment';
import quasarLangSwitcher from '@/services/translation-service.js';
import SessionService from '@/services/session.service';

export const global = {
  namespaced: true,
  state: {
    isMobileDrawerShown: false,
    addedPhotos: null,
    addedPhotosCount: null,
    photosPages: 1,
    navigationOptions: [],
    wallet: 0,
    walletSumOfAllTime: 0,
    registeredCommissions: [],
    bookingCommissions: [],
    withdrawRequests: [],
    withdrawalRequestsSumAmmount: 0,
    lang: 'pl',
    soldedImages: [],
    isLoading: false,
    citiesList: JSON.parse(localStorage.getItem('cities')) || [],
    countriesList: [],
    eventsList: [],
    referralEventsList: [],
    eventsWithConfigurablePricesList: [],
    selectEventsList: [],
    isEventListLoading: false,
    eventListLoadFailed: false,
    eventsTotalCount: 0,
    formData: null,
  },
  getters: {
    getFormData: (state) => state.formData,
    getCountriesList: (state) => state.countriesList,
  },
  actions: {
    saveFormData({ commit }, formData) {
      commit('setFormData', formData);
      SessionService.storeFormData(formData);
    },
    loadFormData({ commit }) {
      const formData = SessionService.getFormData();
      if (formData) {
        commit('setFormData', formData);
      }
    },
    getPageLang({ dispatch }, i18n) {
      api.getLanguage().then((response) => {
        dispatch('setPageLang', { language: response.data, i18n: i18n });
      });
    },
    setPageLang({ commit }, payload) {
      localStorage.setItem('lang', payload.language);
      payload.i18n.locale = payload.language;
      quasarLangSwitcher(payload.language);
      commit('setLanguage', payload.language);
    },
    setDrawerState({ commit }, payload) {
      commit('setDrawerState', payload);
    },
    getAddedPhotos({ commit, rootState }, payload = {}) {
      const page = payload.page || 1;
      const userId = rootState.auth.user.id;
      commit('setIsLoading', true);
      api
        .getAddedPhoto(userId, page)
        .then((response) => {
          commit('setAddedPhotos', response.data.data);
          commit('setAddedPhotosCount', response.data.meta.total);
          commit('setPhotosPages', response.data.meta.last_page);
        })
        .finally(() => {
          commit('setIsLoading', false);
        });
    },
    getNavbarData({ commit }, lang) {
      try {
        api.getNavbarOptions(lang).then((response) => {
          commit('setNavbarOptions', response.data.data);
        });
      } catch (error) {
        console.error(error);
      }
    },
    async getUserWalletData({ commit }) {
      const response = await api.getUserWallet();

      commit('setUserWalletData', response.data.data?.account_balance || null);
      commit(
        'setUserWalletSumOfAllTimeData',
        response.data.data?.account_all_earned_money || null
      );
      commit(
        'setBookingCommissions',
        response.data.data?.booking_commissions || []
      );
      commit(
        'setCommissions',
        response.data.data?.registered_commissions || []
      );
    },
    getWithdrawRequestList({ commit }) {
      api.getWithdrawRequests().then((response) => {
        commit(
          'setWithdrawRequestList',
          response.data.data ? response.data.data.withdrawal_request : []
        );
        commit(
          'setWithdrawRequestSum',
          response.data.data
            ? response.data.data.withdrawal_requests_sum_ammount
            : 0
        );
      });
    },
    getCities({ commit }) {
      api.getSelectCities().then((response) => {
        const citiesData = response.data;
        const cities = citiesData.map((el) => ({
          ...el,
          value: el.id,
          label: el.title.rendered,
        }));
        commit('setCitiesList', cities);
      });
    },
    getCountries({ commit }) {
      api.getSelectCountries().then((response) => {
        const countryData = response.data;
        const countries = countryData.map((el) => ({
          ...el,
          value: el.id,
          label: el.title.rendered,
        }));
        commit('setCountriesList', countries);
      });
    },
    resetUserData({ commit }) {
      commit('clearUserData');
    },
    async getReferralEvents({ commit }) {
      try {
        const response = await api.fetchReferralEvents(1, 20)
        const events = response.data.allRaces.map(el => ({
          ...el,
          value: el.id,
          label: `${el.title} - ${moment(el.eventDate, 'YYYYMMDD').format('YYYY/MM/DD')}`,
        }))
        commit('setReferralEventsList', events)
      } catch (error) {
        console.error('Failed to fetch referral events:', error);
      }
    },
    async getEvents({ commit }) {
      commit('setEventsListFailed', false);
      commit('setEventsListLoading', true);

      try {
        const response = await api.fetchEvents(1, -1)

        const events = response.data.data.map(el => ({
          ...el,
          value: el.id,
          label: `${el.post_title} - ${moment(el.gear_date, 'YYYYMMDD').format('YYYY/MM/DD')}`,
        }))

        commit('setEventsList', events)

        const eventsWithConfigurablePricesList = events.filter(
          event => event.event_type !== 'private'
        );
        commit('setEventsTotalCount', eventsWithConfigurablePricesList.length)
        commit('setEventsWithConfigurablePricesList', eventsWithConfigurablePricesList)
      } catch (error) {
        console.error('Failed to fetch events:', error);
        commit('setEventsListFailed', true);
      } finally {
        commit('setEventsListLoading', false);
      }
    },
    async fetchEventsForSelect({ commit }) {
      commit('setEventsListFailed', false);
      commit('setEventsListLoading', true);
      try {
        const twoWeeksAgo = moment().subtract(14, 'days');
        const startDate = twoWeeksAgo.format('YYYY-MM-DD');

        const response = await api.fetchEvents(1, -1, startDate);
        const selectEventsList = response.data.data.map((el) => ({
          ...el,
          value: el.id,
          label: `${el.post_title} - ${moment(el.gear_date, 'YYYYMMDD').format(
            'YYYY/MM/DD'
          )}`,
        }));
        commit('setSelectEventsList', selectEventsList);
      } catch (error) {
        console.error('Failed to fetch events:', error);
        commit('setEventsListFailed', true);
      } finally {
        commit('setEventsListLoading', false);
      }
    }
  },
  mutations: {
    setFormData(state, formData) {
      state.formData = formData;
    },
    setLanguage(state, payload) {
      state.lang = payload;
    },
    setDrawerState(state, payload) {
      state.isMobileDrawerShown = payload;
    },
    setIsLoading(state, payload) {
      state.isLoading = payload;
    },
    setAddedPhotos(state, payload) {
      state.addedPhotos = payload;
    },
    setAddedPhotosCount(state, payload) {
      state.addedPhotosCount = payload;
    },
    setPhotosPages(state, payload) {
      state.photosPages = payload;
    },
    setNavbarOptions(state, payload) {
      let options = payload;
      const user = localStorage.getItem('user');
      const userParsed = JSON.parse(user);

      if (user && userParsed.role === 'administrator') {
        options.push({
          id: 98,
          name: `addVideo`,
          link: '/add-video',
        });
      }

      options.push({
        id: 99,
        name: `addPhoto`,
        bgClass: 'home-page',
        link: '/',
      });
      options.push({
        id: 100,
        name: `walletProfile`,
        link: '/userPanel?wallet=1',
      });

      const allowedDashboardRoles = ['organizer', 'sponsor', 'administrator', 'photographer'];
      if (user && allowedDashboardRoles.includes(userParsed.role)) {
        options.push({
          id: 101,
          name: 'myEvents',
          link: '/my-events',
        });
      }

      const allowedBookingsRoles = ['organizer', 'photographer'];
      if (user && allowedBookingsRoles.includes(userParsed.role)) {
        options.push({
          id: 102,
          name: 'bookings',
          link: '/bookings',
        });
      }

      state.navigationOptions = options;
    },
    setUserWalletData(state, payload) {
      state.wallet = payload;
    },
    setUserWalletSumOfAllTimeData(state, payload) {
      state.walletSumOfAllTime = payload;
    },
    setCommissions(state, payload) {
      state.registeredCommissions = payload;
      if (payload.length) {
        let tmp = _.map(payload, 'product_album.current_author_images');
        state.soldedImages = tmp.reduce(
          (res, currentArr) => res.concat(currentArr),
          []
        );
      }
    },
    setBookingCommissions(state, payload) {
      state.bookingCommissions = payload;
    },
    setWithdrawRequestList(state, payload) {
      state.withdrawRequests = payload;
    },
    setWithdrawRequestSum(state, payload) {
      state.withdrawalRequestsSumAmmount = payload;
    },
    setCountriesList(state, payload) {
      state.countriesList = payload;
    },
    setCitiesList(state, payload) {
      state.citiesList = payload;
    },
    setEventsList(state, events) {
      state.eventsList = Array.isArray(events) ? events : []
    },
    setEventsWithConfigurablePricesList(state, events) {
      state.eventsWithConfigurablePricesList = Array.isArray(events) ? events : []
    },
    setEventsTotalCount(state, total) {
      state.eventsTotalCount = total;
    },
    setReferralEventsList(state, payload) {
      state.referralEventsList = payload;
    },
    setSelectEventsList(state, payload) {
      state.selectEventsList = payload;
    },
    setEventsListLoading(state, payload) {
      state.isEventListLoading = payload;
    },
    setEventsListFailed(state, payload) {
      state.eventListLoadFailed = payload;
    },
    resetUserData(state) {
      state.citiesList = [];
      state.countriesList = [];
      state.selectEventsList = [];
      state.eventsList = [];
      state.addedPhotos = null;
    },
  },
};
